import { formatNumber, formatCurrency } from 'i18n/format';
import { countLabel } from 'utils/ru';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
  useConnection,
  useCreateBigQuerySource,
  useProbeBigQuery,
  useSourceDomains,
  useUpdateConnection,
} from 'api/connectors';
import { useWarehouses } from 'api/warehouse';
import { extractApiError, localizeApiMessage } from 'api/errors';
import { presetById, presetsFor } from './bigqueryPresets';
import { ColumnCard, ColumnTable, landsAs } from './ColumnPicker';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';

/*
 * BigQuery source wizard (D7, owner decision 13.09).
 *
 *   1. Access   — project + service-account key, checked by a metadata probe.
 *   2. Dataset  — pick one of the datasets the key can actually see.
 *   3. Sources  — tables to load. Daily shards arrive already collapsed into
 *                 one family card, so a year of `events_YYYYMMDD` is one row
 *                 and one stream, not 366.
 *   4. Fields   — which columns each source carries. THIS is why the wizard
 *                 exists: the plain connection form is flat and cannot hold a
 *                 column list, so without this step every stream is SELECT *.
 *   5. Schedule — destination, cron, and an optional backfill with its price.
 *
 * Nothing is saved until step 5 (EVOLV-126: a step-1 save once stranded a
 * half-configured connection with no way to finish), so every probe here runs
 * stateless, on the config in this page's state.
 *
 * Discovery costs nothing: metadata is free and the day estimate is a dry run.
 * Browsing must never appear on the tenant's BigQuery bill.
 *
 * The same page EDITS an existing source (`/connectors/bigquery/:id/edit`).
 * It has to: the generic connection form is flat, so editing a wizard-made
 * connection through it showed one comma-separated `tables` string and no way
 * to reach a column, a per-table window or a key. The differences are three —
 * the stored key is never re-typed (blank means keep, and discovery then runs
 * server-side on the saved connection), the schedule half belongs to the
 * pipeline and is not shown, and the save is a PATCH of the connection alone.
 */

const STEPS = () => ([t('data.access'), t('data.dataset2'), t('data.sources'), t('data.fields'), t('data.schedule')]);

// A whole number of days, 0 included. Blank is not a number — that is the point.
const LOOKBACK_RE = /^\d+$/;

const CRON_PRESETS = [
  { get label() { return t('data.dailyAt3Am'); }, value: '0 3 * * *' },
  { get label() { return t('data.dailyAt7Am'); }, value: '0 7 * * *' },
  { get label() { return t('data.every6Hours'); }, value: '0 */6 * * *' },
  { get label() { return t('data.hourly'); }, value: '0 * * * *' },
];

const DEFAULT_CRON = '0 3 * * *';

// Short on purpose: it lives in a tooltip now, and a tooltip nobody finishes
// reading is a tooltip that explains nothing. What the field is FOR still has
// to cover both destinations — saying only half leaves a lakehouse user
// thinking it does not concern them.
const KEY_PROPERTIES_HELP =
  () => t('data.theColumnsThatIdentifyARowPickSeveralForAComposite');

// $ per TiB scanned, BigQuery on-demand. Only ever used to show an order of
// magnitude next to a chosen window — never to bill anything.
const USD_PER_TIB = 6.25;
const TIB = 1024 ** 4;

const PROBE_ERROR_HINTS = {
  get auth() { return t('data.bigqueryRefusedTheKeyCheckThatTheServiceAccountHasBigquery'); },
  get not_found() { return t('data.noSuchProjectDatasetOrTableIfTheDataLivesIn'); },
  get unreachable() { return t('data.bigqueryDidNotAnswerThisIsRetryableAndDoesNotMark'); },
  get rate_limit() { return t('data.tooManyMetadataRequestsInARowThisIsAQuota'); },
  get config() { return t('data.theKeyOrANameIsMalformedSeeTheMessageAbove'); },
};

function bytesLabel(bytes) {
  if (!bytes && bytes !== 0) return null;
  const mib = bytes / 1024 ** 2;
  return mib >= 1024 ? `${formatNumber(mib / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} GiB` : `${formatNumber(mib, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MiB`;
}

function moneyLabel(bytes) {
  const usd = (bytes / TIB) * USD_PER_TIB;
  return usd < 0.01 ? `< ${formatCurrency(0.01, 'USD')}` : `≈ ${formatCurrency(usd, 'USD')}`;
}

function daysBetween(from, to) {
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
  return Math.round((end - start) / 86_400_000);
}

// `events_*` → `events`, the stream and warehouse table name.
function suggestStreamName(table) {
  const base = table.endsWith('*') ? table.slice(0, -1) : table;
  return base.replace(/_+$/, '').toLowerCase();
}

let columnUid = 0;

function columnRow(entry) {
  columnUid += 1;
  return { uid: columnUid, source: '', expression: '', alias: '', type: '', ...entry };
}

/**
 * One saved source back into the shape this page edits.
 *
 * `columns` stays null — that is the table's catalog, which only discovery
 * knows. What is restored here is the CHOICE: which columns were ticked, what
 * they were renamed to, and the expressions someone wrote by hand.
 */
function savedSource(entry) {
  const saved = entry.columns || [];
  const aliases = {};
  const selected = [];
  const extra = [];
  saved.forEach((column) => {
    if (column.source) {
      selected.push(column.source);
      // An alias equal to the dotted path with dots swapped is the one the
      // server fills in by itself; keeping it would show every column as
      // renamed to what it already is.
      if (column.alias && column.alias !== column.source.replace(/\./g, '_')) {
        aliases[column.source] = column.alias;
      }
    } else if (column.expression) {
      extra.push(
        columnRow({
          expression: column.expression,
          alias: column.alias || '',
          type: column.type || '',
        }),
      );
    }
  });
  return {
    table: entry.table,
    name: entry.name || suggestStreamName(entry.table),
    // The same rule discovery uses, so the Sources step draws the right
    // control before anything has been read.
    kind: entry.table.endsWith('*') ? 'family' : 'table',
    shards: 0,
    columns: null, // not read yet — see `hydrate`
    candidates: [],
    requiresPartitionFilter: false,
    bytesPerDay: null,
    windowColumn: entry.window_column || '',
    // No saved columns at all means the stream is SELECT *, which is what
    // `null` says here. An empty list would mean "nothing chosen".
    selected: saved.length ? selected : null,
    aliases,
    extra,
    manual: selected,
    presets: [],
    keyProperties: asKeys(entry.key_properties),
  };
}

// The key is a list of column names. It was a comma-separated string until the
// picker replaced the text field, and a page kept alive across that change (a
// dev hot-reload, a tab left open through a deploy) still holds the old shape —
// so read it through here rather than trusting the type.
function asKeys(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    return value.split(',').map((name) => name.trim()).filter(Boolean);
  }
  return [];
}


export default function BigQuerySourceWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { connectionId } = useParams();
  const editing = Boolean(connectionId);
  const probe = useProbeBigQuery();
  const createSource = useCreateBigQuerySource();
  const updateConnection = useUpdateConnection(connectionId);
  const { data: connection } = useConnection(connectionId);
  const { data: warehouses } = useWarehouses();
  const { data: domains } = useSourceDomains();

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);

  // Step 1 — access.
  const [access, setAccess] = useState({
    project_id: '',
    credentials_json: '',
    dataset_project: '',
    location: '',
  });
  const [datasets, setDatasets] = useState(null); // null = not checked yet
  // A key scoped to exactly one dataset cannot enumerate the project's
  // datasets, and never will. That is a correct key, not a broken one — the
  // picker just becomes a text field.
  const [canList, setCanList] = useState(true);

  // Step 2 — dataset, and what is in it.
  const [dataset, setDataset] = useState('');
  const [tables, setTables] = useState(null);

  // Step 3/4 — chosen sources, keyed by table name.
  const [sources, setSources] = useState({});
  // Step 4 — the column filter, per source. A GA4 export has a few hundred
  // columns and the list is one per line, so finding one by scrolling is not
  // a thing a person should have to do.
  const [filters, setFilters] = useState({});
  // The column the mouse is over, with its source, described in that
  // source's rail only.
  const [inspect, setInspect] = useState(null);
  // Which table is being described right now. Selecting one costs a metadata
  // round-trip (columns, axis, a day's size), and without a mark on the row
  // the whole step looks frozen — the tick appears and then nothing moves.
  const [describing, setDescribing] = useState(null);

  // Step 5 — destination and cadence.
  const [connectionName, setConnectionName] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [cron, setCron] = useState(DEFAULT_CRON);
  // How far behind the bookmark a scheduled run restarts.
  // Deliberately blank. A number nobody chose either loses late rows or pays
  // to re-read days that never change, and both failures are silent — so the
  // wizard asks instead of guessing.
  const [lookbackDays, setLookbackDays] = useState('');
  // Set by a preset, so the schedule step can say where the number came from.
  const [lookbackFromPreset, setLookbackFromPreset] = useState('');
  // The template domain this data feeds (dbt_source_key). A GA4 export keys
  // on `ga4`, which is what builds the GA4 marts and reports. Offered once for
  // a table that looks like an export, never over a choice already made.
  const [domain, setDomain] = useState('');
  const domainChosen = useRef(false);
  const [backfill, setBackfill] = useState({ enabled: false, start: '', end: '' });
  // The first day a scheduled run may read. Not a nicety: without it — and
  // without a bookmark, which a new pipeline has not got — the platform
  // refuses to start the run at all rather than read the source's whole
  // history. Yesterday by default, so the source starts current and history
  // stays the explicit, priced choice below. Yesterday and not today because a
  // daily-sharded export has no shard for today until today is over, and a
  // first load that reads nothing looks broken rather than current.
  const [startDate, setStartDate] = useState(() =>
    new Date(Date.now() - 86_400_000).toISOString().slice(0, 10),
  );

  // Fill the wizard from the saved connection, once. `columns` is left null
  // on purpose: it is the table's CATALOG, which only discovery knows, and
  // re-reading it for every source the moment the page opens would spend a
  // round-trip per source on someone who came to change a cron. It is fetched
  // on the way into the Fields step instead.
  const prefilled = useRef(false);
  useEffect(() => {
    const saved = connection?.bigquery_sources;
    if (!editing || prefilled.current || !saved) return;
    prefilled.current = true;
    setAccess({
      project_id: saved.project_id || '',
      credentials_json: '',
      dataset_project: saved.dataset_project || '',
      location: saved.location || '',
    });
    setDataset(saved.dataset || '');
    setConnectionName(connection.name || '');
    setDomain(connection.dbt_source_key || '');
    domainChosen.current = true;
    setStartDate(saved.start_date || '');
    setLookbackDays(
      saved.lookback_days === null || saved.lookback_days === undefined
        ? ''
        : String(saved.lookback_days),
    );
    setSources(
      Object.fromEntries(
        (saved.sources || []).map((entry) => [entry.table, savedSource(entry)]),
      ),
    );
  }, [connection, editing]);

  // Edit mode lands on a dataset that is already chosen, so opening it is a
  // click that can only be answered one way. The ref keeps a failed probe from
  // retrying itself forever.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!editing || autoOpened.current) return;
    if (activeStep !== 1 || !dataset || tables || probe.isPending) return;
    autoOpened.current = true;
    openDataset(dataset);
    // openDataset is stable enough for this one-shot: it reads state that is
    // already settled by the time the Dataset step is on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, activeStep, dataset, tables]);

  const activeWarehouses = useMemo(
    () => (warehouses || []).filter((w) => w.is_active),
    [warehouses],
  );
  const chosen = useMemo(() => Object.values(sources), [sources]);

  const config = () => ({
    project_id: access.project_id.trim(),
    credentials_json: access.credentials_json,
    ...(access.dataset_project.trim()
      ? { dataset_project: access.dataset_project.trim() }
      : {}),
    ...(access.location.trim() ? { location: access.location.trim() } : {}),
  });

  // Editing, with the stored key untouched: the browser has no key to send,
  // so discovery runs on the SAVED connection with the non-secret fields being
  // edited layered on top. Paste a new key and it is probed directly instead —
  // the point of pasting one is to find out whether it works.
  const probeRoute = () =>
    editing && !access.credentials_json.trim()
      ? {
          connectionId,
          overrides: {
            project_id: access.project_id.trim(),
            dataset_project: access.dataset_project.trim(),
          },
        }
      : { config: config() };

  const runProbe = async (asked, onResult) => {
    setError(null);
    const result = await probe.mutateAsync({ ...probeRoute(), ...asked });
    if (!result.reachable) {
      setError(
        PROBE_ERROR_HINTS[result.error] || localizeApiMessage(result.hint, null, t('data.discoveryFailed')),
      );
      return null;
    }
    onResult?.(result);
    return result;
  };

  // ── step 1 ─────────────────────────────────────────────────────────────────

  const checkAccess = async () => {
    const result = await runProbe({}, (r) => {
      setDatasets(r.datasets);
      setCanList(r.can_list !== false);
    });
    if (result) setConnectionName((name) => name || t('data.bigqueryValue', { value1: result.project }));
  };

  // ── step 2 ─────────────────────────────────────────────────────────────────

  const openDataset = async (name) => {
    // Only a DIFFERENT dataset invalidates the chosen sources. Re-opening the
    // one already saved is what edit mode does on the way in, and clearing
    // here would drop the very selection the page exists to show.
    if (name !== dataset) setSources({});
    setDataset(name);
    setTables(null);
    await runProbe({ dataset: name }, (r) => setTables(r.tables));
  };

  // Sources restored from the saved config carry a selection but no catalog:
  // what columns the table has, what a day costs, which axes it offers. Fill
  // that in before the Fields step, which has nothing to draw without it.
  const hydrate = async () => {
    for (const source of chosen) {
      if (source.columns) continue;
      setDescribing(source.table);
      let detail;
      try {
        // eslint-disable-next-line no-await-in-loop -- one metadata call per
        // source, in order, so the spinner names the row being read.
        detail = await runProbe({ dataset, table: source.table });
      } finally {
        setDescribing(null);
      }
      if (!detail) return false;
      updateSource(source.table, {
        kind: detail.kind,
        shards: detail.shards,
        columns: detail.columns,
        candidates: detail.window_candidates,
        requiresPartitionFilter: detail.requires_partition_filter,
        bytesPerDay: detail.bytes_per_day,
      });
    }
    return true;
  };

  const goNext = async () => {
    if (activeStep === 2 && chosen.some((source) => !source.columns)) {
      if (!(await hydrate())) return;
    }
    setActiveStep((step) => step + 1);
  };

  // ── step 3 ─────────────────────────────────────────────────────────────────

  const toggleSource = async (table) => {
    if (sources[table.name]) {
      setSources(({ [table.name]: _dropped, ...rest }) => rest);
      return;
    }
    // Describing the table is what fills the next step: its columns, the axis
    // to suggest, and what a day of it costs.
    setDescribing(table.name);
    let detail;
    try {
      detail = await runProbe({ dataset, table: table.name });
    } finally {
      setDescribing(null);
    }
    if (!detail) return;
    setSources((current) => ({
      ...current,
      [table.name]: {
        table: table.name,
        name: suggestStreamName(table.name),
        kind: detail.kind,
        shards: detail.shards,
        columns: detail.columns,
        candidates: detail.window_candidates,
        requiresPartitionFilter: detail.requires_partition_filter,
        bytesPerDay: detail.bytes_per_day,
        // A family needs no axis: the shard IS the day. Otherwise take the
        // partitioning column, which discovery already put first.
        windowColumn: detail.kind === 'family' ? '' : detail.window_candidates[0] || '',
        selected: null, // null = every column
        extra: [],
        // Most sources have none: an event stream is not unique by anything,
        // and there the day replacement is what makes a re-run safe.
        keyProperties: [],
      },
    }));
  };

  const updateSource = (table, patch) =>
    setSources((current) => ({ ...current, [table]: { ...current[table], ...patch } }));

  // Re-price a day once an axis is chosen: without one there is no "day" to
  // price, so discovery answered null the first time.
  const chooseAxis = async (table, column) => {
    updateSource(table, { windowColumn: column });
    if (!column) return;
    setDescribing(table);
    let detail;
    try {
      detail = await runProbe({ dataset, table, windowColumn: column });
    } finally {
      setDescribing(null);
    }
    if (detail) updateSource(table, { bytesPerDay: detail.bytes_per_day });
  };

  // ── step 4 ─────────────────────────────────────────────────────────────────

  const toggleColumn = (table, path) => {
    const source = sources[table];
    const all = source.columns.map((c) => c.path);
    const current = source.selected === null ? all : source.selected;
    const had = current.includes(path);
    const next = had ? current.filter((p) => p !== path) : [...current, path];
    // Remembered so that removing a preset cannot untick something the human
    // ticked themselves, even when the preset happens to name it too.
    const manual = had
      ? (source.manual || []).filter((p) => p !== path)
      : [...(source.manual || []), path];
    updateSource(table, {
      selected: next.length === all.length ? null : next,
      manual,
    });
  };

  const setAll = (table, on) =>
    updateSource(table, { selected: on ? null : [], manual: [] });

  // Presets stack (owner, 13.09). Each is a SET of rows: turning one on adds
  // its rows to whatever is already ticked, and turning it off takes back only
  // the rows no other applied preset — and no hand tick — still asks for.
  const togglePreset = (table, preset) => {
    const source = sources[table];
    const paths = new Set(source.columns.map((c) => c.path));
    const applied = source.presets || [];
    const turningOn = !applied.includes(preset.id);
    const nowApplied = turningOn
      ? [...applied, preset.id]
      : applied.filter((id) => id !== preset.id);

    const rows = preset.columns(paths);
    // `selected === null` is "every column", and a preset exists to narrow —
    // so it composes with an empty selection, not with all of them.
    const base = source.selected === null ? [] : source.selected;
    const aliases = { ...(source.aliases || {}) };
    let selected;
    let extra;

    if (turningOn) {
      const add = rows.filter((r) => r.source).map((r) => r.source);
      selected = [...base, ...add.filter((path) => !base.includes(path))];
      const have = new Set(source.extra.map((r) => r.alias));
      extra = [
        ...source.extra,
        ...rows.filter((r) => r.expression && !have.has(r.alias)).map(columnRow),
      ];
      // Dotted paths a preset names are real columns, but the tick list is
      // flat, so their short names ride along as aliases.
      rows
        .filter((r) => r.source && r.alias)
        .forEach((r) => {
          aliases[r.source] = r.alias;
        });
    } else {
      const kept = nowApplied.flatMap((id) => presetById(id)?.columns(paths) || []);
      const keptPaths = new Set(kept.filter((r) => r.source).map((r) => r.source));
      const keptAliases = new Set(
        kept.filter((r) => r.expression).map((r) => r.alias),
      );
      const manual = source.manual || [];
      const drop = new Set(
        rows
          .filter((r) => r.source)
          .map((r) => r.source)
          .filter((path) => !keptPaths.has(path) && !manual.includes(path)),
      );
      const dropAliases = new Set(
        rows
          .filter((r) => r.expression)
          .map((r) => r.alias)
          .filter((alias) => !keptAliases.has(alias)),
      );
      selected = base.filter((path) => !drop.has(path));
      extra = source.extra.filter((row) => !dropAliases.has(row.alias));
      drop.forEach((path) => delete aliases[path]);
    }

    // Nothing is asked for any more: back to every column, where the source
    // started. An empty list would mean the same thing on save and read as
    // "I chose nothing", which is a worse way to say it.
    const empty = !selected.length && !extra.length;
    const next = {
      ...sources,
      [table]: {
        ...source,
        presets: nowApplied,
        selected: empty ? null : selected,
        extra,
        aliases,
      },
    };
    setSources(next);
    syncPresetLookback(next);
  };

  // The lookback belongs to the connection, not to one source, so it follows
  // whatever the applied presets ask for across all of them — the longest
  // window wins, and it is cleared again when the last preset that asked for
  // one is switched off. A number the human typed is never touched.
  const syncPresetLookback = (bySource) => {
    const asked = Object.values(bySource)
      .flatMap((source) => source.presets || [])
      .map(presetById)
      .filter((preset) => preset && preset.lookbackDays !== undefined);
    if (!asked.length) {
      if (lookbackFromPreset) {
        setLookbackDays('');
        setLookbackFromPreset('');
      }
      return;
    }
    const widest = asked.reduce((a, b) => (b.lookbackDays > a.lookbackDays ? b : a));
    setLookbackDays(String(widest.lookbackDays));
    setLookbackFromPreset(widest.label);
  };

  const columnsPayload = (source) => {
    const aliases = source.aliases || {};
    const picked =
      source.selected === null
        ? []
        : source.selected.map((path) => ({
            source: path,
            ...(aliases[path] ? { alias: aliases[path] } : {}),
          }));
    const expressions = source.extra
      .filter((row) => row.expression.trim() && row.alias.trim())
      .map((row) => ({
        expression: row.expression.trim(),
        alias: row.alias.trim(),
        type: row.type.trim() || 'string',
      }));
    return [...picked, ...expressions];
  };

  // ── step 5 ─────────────────────────────────────────────────────────────────

  // History and the schedule have to meet. The backfill's end is exclusive --
  // the first day it does NOT load -- so the two line up exactly when the
  // start date IS that day.
  const gapNote = useMemo(() => {
    if (!backfill.enabled || !backfill.end || backfill.end === startDate) return '';
    return backfill.end < startDate
      ? t('data.nothingLoadsBetweenValueAndValueHistoryStopsBeforeTheSchedule', { value1: backfill.end, value2: startDate, value3: backfill.end })
      : t('data.historyAlreadyCoversUpToValueSoTheFirstScheduledRuns', { value1: backfill.end, value2: backfill.end });
  }, [backfill.enabled, backfill.end, startDate, t]);

  const estimate = useMemo(() => {
    const days = daysBetween(backfill.start, backfill.end);
    const perDay = chosen.reduce((sum, s) => sum + (s.bytesPerDay || 0), 0);
    const unknown = chosen.some((s) => !s.bytesPerDay);
    return { days, perDay, bytes: days * perDay, unknown };
  }, [backfill.start, backfill.end, chosen]);

  const saving = createSource.isPending || updateConnection.isPending;

  useEffect(() => {
    if (activeStep !== 4 || domainChosen.current || !(domains || []).includes('ga4')) return;
    domainChosen.current = true;
    const ga4Export = chosen.some((source) =>
      presetsFor(source.columns).some((preset) => preset.id === 'ga4_core'),
    );
    if (ga4Export) setDomain('ga4');
  }, [activeStep, chosen, domains]);

  // What the connection itself owns, in both modes. On an edit the blank key
  // and any blank optional field mean "keep what is stored" — the merge
  // contract every connector edit in this console uses.
  const connectionConfig = () => ({
    ...config(),
    dataset,
    start_date: startDate,
    lookback_days: Number(lookbackDays),
    sources: chosen.map((source) => {
      const columns = columnsPayload(source);
      return {
        table: source.table,
        name: source.name,
        ...(source.windowColumn ? { window_column: source.windowColumn } : {}),
        ...(columns.length ? { columns } : {}),
        ...(asKeys(source.keyProperties).length
          ? { key_properties: asKeys(source.keyProperties) }
          : {}),
      };
    }),
  });

  const saveEdit = async () => {
    setError(null);
    try {
      await updateConnection.mutateAsync({
        name: connectionName.trim(),
        config: connectionConfig(),
        dbt_source_key: domain,
      });
      navigate(`/console/connectors/${connectionId}`);
    } catch (exc) {
      setError(extractApiError(exc));
    }
  };

  const save = async () => {
    if (editing) return saveEdit();
    setError(null);
    const payload = {
      connection: {
        name: connectionName.trim(),
        connector_type_slug: 'bigquery',
        config: connectionConfig(),
        dbt_source_key: domain,
      },
      pipeline: {
        name: t('data.warehouseFlow', { name: connectionName.trim() }),
        warehouse_id: warehouseId,
        schedule_cron: cron,
      },
      ...(backfill.enabled && estimate.days
        ? {
            backfill: {
              start_date: backfill.start,
              end_date: backfill.end,
              slice_days: 1,
            },
          }
        : {}),
    };
    try {
      await createSource.mutateAsync(payload);
      navigate('/console/pipelines');
    } catch (exc) {
      setError(extractApiError(exc));
    }
  };

  const canLeaveStep = [
    // Editing starts from a connection that already works, so nothing has to
    // be proven again before moving on.
    editing || Boolean(datasets),
    Boolean(dataset && tables),
    chosen.length > 0 && chosen.every((s) => s.name),
    true, // fields are optional — every column is a valid answer
    Boolean(
      connectionName.trim() &&
        (editing || warehouseId) &&
        startDate &&
        LOOKBACK_RE.test(lookbackDays),
    ),
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton
          onClick={() =>
            navigate(
              editing ? `/console/connectors/${connectionId}` : '/console/connectors',
            )
          }
          size="small"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          {editing ? connectionName || t('data.bigquerySource') : t('data.bigquerySource')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t('data.browsingCostsNothingEverythingBelowReadsMetadataAndTheDayEstimate')}{' '}
        {editing
          ? t('data.nothingChangesUntilYouSaveOnTheLastStep')
          : t('data.nothingIsSavedUntilTheLastStep')}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4, maxWidth: 720 }}>
        {STEPS().map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, maxWidth: 720 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {activeStep === 0 && (
        <Box sx={{ maxWidth: 720 }}>
          <TextField
            label={t('data.gcpProject')}
            helperText={t('data.whereTheQueryRunsAndTheProjectBilledForTheBytes')}
            value={access.project_id}
            onChange={(e) => setAccess({ ...access, project_id: e.target.value })}
            required
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={editing ? t('data.serviceAccountJsonLeaveBlankToKeep') : t('data.serviceAccountJson')}
            helperText={
              editing
                ? t('data.theStoredKeyNeverLeavesTheServerSoItCannotBe')
                : t('data.theFullKeyFileNeedsBigqueryUserOnTheProjectAbove')
            }
            value={access.credentials_json}
            onChange={(e) => setAccess({ ...access, credentials_json: e.target.value })}
            required={!editing}
            fullWidth
            multiline
            minRows={editing ? 2 : 4}
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('data.datasetProjectOptional')}
            helperText={t('data.onlyWhenTheTablesLiveInAnotherProjectADatasetShared')}
            value={access.dataset_project}
            onChange={(e) => setAccess({ ...access, dataset_project: e.target.value })}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('data.locationOptional')}
            helperText={t('data.euUsEuropeWest3BlankLetsBigqueryDecide')}
            value={access.location}
            onChange={(e) => setAccess({ ...access, location: e.target.value })}
            fullWidth
            sx={{ mb: 3 }}
          />
          <Button
            variant="outlined"
            startIcon={probe.isPending ? <CircularProgress size={16} /> : <TravelExploreIcon />}
            disabled={
              !access.project_id.trim() ||
              (!editing && !access.credentials_json) ||
              probe.isPending
            }
            onClick={checkAccess}
          >{t('data.checkAccess')}</Button>
          {datasets && canList && (
            <Alert icon={<CheckCircleIcon />} severity="success" sx={{ mt: 2 }}>{t('data.theKeyCanSee')}{' '}{countLabel(datasets.length, 'dataset')}.
            </Alert>
          )}
          {datasets && !canList && (
            <Alert icon={<CheckCircleIcon />} severity="success" sx={{ mt: 2 }}>{t('data.theKeyWorksItCanRunQueriesInThisProjectIt')}</Alert>
          )}
        </Box>
      )}

      {activeStep === 1 && (
        <Box sx={{ maxWidth: 720 }}>
          {datasets && canList ? (
            <Autocomplete
              options={datasets || []}
              value={dataset || null}
              onChange={(_e, value) => value && openDataset(value)}
              renderInput={(params) => (
                <TextField {...params} label={t('data.dataset2')} required autoFocus />
              )}
            />
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label={t('data.dataset2')}
                helperText={
                  editing
                    ? t('data.changeItToPointThisSourceAtAnotherDataset')
                    : t('data.thisKeyCannotListDatasetsSoNameTheOneYouAre')
                }
                value={dataset}
                onChange={(e) => setDataset(e.target.value)}
                required
                autoFocus
                fullWidth
              />
              <Button
                variant="outlined"
                sx={{ height: 56 }}
                disabled={!dataset.trim() || probe.isPending}
                onClick={() => openDataset(dataset.trim())}
              >{t('data.open')}</Button>
            </Box>
          )}
          {probe.isPending && <CircularProgress size={20} sx={{ mt: 2 }} />}
          {tables && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {countLabel(tables.length, 'source')}{' '}{t('data.found')}</Typography>
          )}
        </Box>
      )}

      {activeStep === 2 && (
        <Box sx={{ maxWidth: 860 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.dailyShardsAreAlreadyGroupedAFamilyLike')}{' '}<code>events_*</code>{' '}{t('data.isOneSourceHereAndOneTableInTheWarehouseLoaded')}</Typography>
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            {(tables || []).map((table) => {
              const source = sources[table.name];
              return (
                <Box key={table.name} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {describing === table.name ? (
                      <Box sx={{ width: 42, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={18} />
                      </Box>
                    ) : (
                      <Checkbox
                        checked={Boolean(source)}
                        onChange={() => toggleSource(table)}
                        disabled={Boolean(describing)}
                      />
                    )}
                    <Typography sx={{ fontFamily: 'monospace', minWidth: 260 }}>
                      {table.name}
                    </Typography>
                    {describing === table.name && (
                      <Typography variant="caption" color="text.secondary">{t('data.readingItsColumns')}</Typography>
                    )}
                    <Chip size="small" variant="outlined" label={table.kind} />
                    {table.kind === 'family' && (
                      <Typography variant="caption" color="text.secondary">
                        {table.shards}{' '}{t('data.days')}{' '}{table.first_shard} → {table.last_shard}
                      </Typography>
                    )}
                  </Box>
                  {source && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 1.5, ml: 5, alignItems: 'flex-start' }}>
                      <TextField
                        size="small"
                        label={t('data.tableInTheWarehouse')}
                        value={source.name}
                        onChange={(e) => updateSource(table.name, { name: e.target.value })}
                        sx={{ minWidth: 240 }}
                      />
                      {source.kind === 'family' ? (
                        <Tooltip title={t('data.theShardIsTheDayEachLoadReadsOnlyTheDays')}>
                          <Chip size="small" label={t('data.windowedByShardDate')} sx={{ mt: 1 }} />
                        </Tooltip>
                      ) : (
                        <TextField
                          select
                          size="small"
                          label={t('data.timeColumn')}
                          value={source.windowColumn}
                          onChange={(e) => chooseAxis(table.name, e.target.value)}
                          helperText={
                            (source.candidates || []).length
                              ? t('data.theFirstIsThePartitioningColumn')
                              : t('data.noDateColumnTheTableReloadsWholeEachRun')
                          }
                          sx={{ minWidth: 240 }}
                        >
                          <MenuItem value="">
                            <em>{t('data.reloadWholeEveryRun')}</em>
                          </MenuItem>
                          {(source.candidates || []).map((column) => (
                            <MenuItem key={column} value={column}>
                              {column}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      {source.bytesPerDay ? (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
                          ~{bytesLabel(source.bytesPerDay)}{' '}{t('data.aDay')}</Typography>
                      ) : null}
                    </Box>
                  )}
                  {source?.requiresPartitionFilter && !source.windowColumn && (
                    <Alert severity="warning" sx={{ mt: 1.5, ml: 5 }}>{t('data.thisTableRefusesQueriesThatCannotPrunePartitionsChooseItsTime')}</Alert>
                  )}
                </Box>
              );
            })}
          </Paper>
        </Box>
      )}

      {activeStep === 3 && (
        <Box sx={{ maxWidth: 1160 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.leaveASourceUntouchedToCarryEveryColumnNarrowingItReads')}</Typography>
          {chosen.map((source) => {
            const all = (source.columns || []).map((c) => c.path);
            const picked = source.selected === null ? all : source.selected;
            // Once per source, not `includes` per row: at 171 columns that
            // was 29 000 string comparisons on every keystroke and every tick.
            const pickedSet = new Set(picked);
            const presets = presetsFor(source.columns);
            const applied = source.presets || [];
            const needle = (filters[source.table] || '').trim().toLowerCase();
            const shown = needle
              ? (source.columns || []).filter(
                  (column) =>
                    column.path.toLowerCase().includes(needle) ||
                    (column.description || '').toLowerCase().includes(needle),
                )
              : source.columns || [];
            const expressions = source.extra.filter(
              (row) => row.expression.trim() && row.alias.trim(),
            );
            // A key names a column of the OUTPUT, so the choices are the names
            // this source will actually emit — not the paths on the left of the
            // list. Offering anything else invites a key that does not exist,
            // which the loader refuses at load time rather than here.
            const keyOptions = [
              ...picked.map((path) => landsAs(path, (source.aliases || {})[path])),
              ...expressions.map((row) => row.alias.trim()),
            ].filter((name, index, names) => names.indexOf(name) === index);
            const keys = asKeys(source.keyProperties);
            // A column can be unticked after it was made part of the key. Not
            // while the catalog is still being read, though: a saved source
            // that carries every column has nothing to compare against until
            // then, and every key it has would look wrong.
            const staleKeys = source.columns
              ? keys.filter((k) => !keyOptions.includes(k))
              : [];
            return (
              <Paper key={source.table} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography sx={{ fontFamily: 'monospace' }}>{source.table}</Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      source.selected === null
                        ? t('data.allValueColumns', { value1: all.length })
                        : t('data.valueOfValue', { value1: picked.length, value2: all.length })
                    }
                  />
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mt: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={source.selected === null}
                            indeterminate={
                              source.selected !== null && picked.length > 0
                            }
                            onChange={(e) => setAll(source.table, e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="caption">
                            {source.selected === null ? t('data.clearAll') : t('data.selectAll')}
                          </Typography>
                        }
                      />
                      <TextField
                        size="small"
                        placeholder={t('data.filterByNameOrDescription')}
                        value={filters[source.table] || ''}
                        onChange={(e) =>
                          setFilters({ ...filters, [source.table]: e.target.value })
                        }
                        sx={{ width: 280 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Box sx={{ flex: 1 }} />
                      {needle && (
                        <Typography variant="caption" color="text.secondary">
                          {shown.length}{' '}{t('data.of')}{' '}{all.length}{' '}{t('data.shown')}</Typography>
                      )}
                    </Box>
                    <ColumnTable
                      columns={shown}
                      picked={pickedSet}
                      onToggle={(path) => toggleColumn(source.table, path)}
                      onInspect={(column) =>
                        setInspect(column ? { table: source.table, column } : null)
                      }
                      emptyNote="No column matches that filter."
                    />
                    {source.selected !== null && !picked.length && !expressions.length && (
                      <Alert severity="warning" sx={{ mt: 1.5 }}>{t('data.nothingIsTickedSoThisSourceWouldLoadEveryColumnAn')}</Alert>
                    )}
                    <Divider sx={{ my: 1.5 }} />
                    {source.extra.map((row, index) => (
                      <Box key={row.uid} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          size="small"
                          label={t('data.sqlExpression')}
                          value={row.expression}
                          onChange={(e) => {
                            const extra = [...source.extra];
                            extra[index] = { ...row, expression: e.target.value };
                            updateSource(source.table, { extra });
                          }}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          label={t('data.name')}
                          value={row.alias}
                          onChange={(e) => {
                            const extra = [...source.extra];
                            extra[index] = { ...row, alias: e.target.value };
                            updateSource(source.table, { extra });
                          }}
                          sx={{ width: 180 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            updateSource(source.table, {
                              extra: source.extra.filter((r) => r.uid !== row.uid),
                            })
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() =>
                        updateSource(source.table, {
                          extra: [...source.extra, columnRow({})],
                        })
                      }
                    >{t('data.addExpression')}</Button>

                    <Divider sx={{ my: 1.5 }} />
                    {/* Full width, and below everything it depends on: the
                        choices ARE the columns ticked above plus the
                        expressions written above, so it reads in the order it
                        is filled. */}
                    <Autocomplete
                      multiple
                      size="small"
                      disableCloseOnSelect
                      options={keyOptions}
                      value={keys}
                      onChange={(_e, value) =>
                        updateSource(source.table, { keyProperties: value })
                      }
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('data.primaryKey')}
                          placeholder={
                            keys.length
                              ? ''
                              : t('data.usuallyNonePickOneOrSeveralColumnsForACompositeKey')
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                <Tooltip title={KEY_PROPERTIES_HELP()}>
                                  <HelpOutlineIcon fontSize="small" color="disabled" />
                                </Tooltip>
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    {staleKeys.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 1.5 }}>{t('data.theKeyNames')}{' '}{staleKeys.join(', ')}{t('data.whichThisSourceNoLongerEmitsTick')}{' '}
                        {staleKeys.length === 1 ? t('data.it2') : t('data.them2')}{' '}{t('data.againOrDrop')}{' '}
                        {staleKeys.length === 1 ? t('data.it2') : t('data.them2')}{' '}{t('data.fromTheKeyALoadStopsWhenADeclaredKeyColumn')}</Alert>
                    )}
                  </Box>

                  <Box sx={{ width: 264, flexShrink: 0 }}>
                    {presets.length > 0 && (
                      <>
                        <Typography variant="overline" color="text.secondary">{t('data.presets')}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                          {presets.map((preset) => (
                            <Tooltip key={preset.id} title={preset.describe}>
                              <Chip
                                size="small"
                                label={preset.label}
                                color={applied.includes(preset.id) ? 'primary' : 'default'}
                                variant={
                                  applied.includes(preset.id) ? 'filled' : 'outlined'
                                }
                                onClick={() => togglePreset(source.table, preset)}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 1.5 }}
                        >{t('data.theyStackEachOneAddsItsColumnsAndSwitchingOneOff')}</Typography>
                        <Divider sx={{ my: 2 }} />
                      </>
                    )}
                    <Typography variant="overline" color="text.secondary">{t('data.column')}</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <ColumnCard
                        column={inspect?.table === source.table ? inspect.column : null}
                        alias={
                          inspect?.table === source.table
                            ? (source.aliases || {})[inspect.column.path]
                            : undefined
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {activeStep === 4 && (
        <Box sx={{ maxWidth: 720 }}>
          <TextField
            label={t('data.connectionName')}
            value={connectionName}
            onChange={(e) => setConnectionName(e.target.value)}
            required
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            select
            label={t('data.reportDomain')}
            value={domain}
            onChange={(e) => {
              domainChosen.current = true;
              setDomain(e.target.value);
            }}
            fullWidth
            sx={{ mb: 2 }}
            helperText={t('data.forAGA4ExportChooseGa4TheGA4MartsAnd')}
          >
            <MenuItem value="">{t('data.notMapped')}</MenuItem>
            {(domains || []).map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </TextField>
          {editing ? (
            <Alert severity="info" sx={{ mb: 3 }}>{t('data.destinationScheduleAndHistoryBelongToThePipelineNotToThis')}</Alert>
          ) : null}
          {!editing && (
            <>
              <TextField
                select
                label={t('data.destinationWarehouse')}
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                required
                fullWidth
                sx={{ mb: 2 }}
              >
                {activeWarehouses.map((w) => (
                  <MenuItem key={w.public_id} value={w.public_id}>
                    {w.name}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({w.engine_display})
                    </Typography>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('data.schedule')}
                value={cron}
                onChange={(e) => setCron(e.target.value)}
                fullWidth
                sx={{ mb: 3 }}
              >
                {CRON_PRESETS.map((preset) => (
                  <MenuItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          <TextField
            type="date"
            label={t('data.startLoadingFrom')}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            error={!startDate}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 3 }}
            helperText={
              backfill.enabled && backfill.end && backfill.end !== startDate
                ? gapNote
                : t('data.theFirstDayAScheduledRunMayReadRequiredAPipeline')
            }
          />

          <TextField
            type="number"
            label={t('data.reReadTheLastNDaysOnEveryRunRequired')}
            value={lookbackDays}
            onChange={(e) => {
              setLookbackDays(e.target.value.replace(/[^0-9]/g, ''));
              setLookbackFromPreset('');
            }}
            required
            error={lookbackDays !== '' && !LOOKBACK_RE.test(lookbackDays)}
            inputProps={{ min: 0, max: 30 }}
            fullWidth
            sx={{ mb: 3 }}
            helperText={
              lookbackFromPreset
                ? t('data.setByValueThisExportWritesLateEventsIntoDaysIt', { value1: lookbackFromPreset })
                : t('data.0ForASourceThatNeverChangesADayOnceWritten')
            }
          />

          {!editing && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={backfill.enabled}
                    onChange={(e) =>
                      setBackfill({ ...backfill, enabled: e.target.checked })
                    }
                  />
                }
                label={t('data.loadHistoryNow')}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 2 }}
              >{t('data.offByDefaultHistoryIsLoadedADayAtATime')}</Typography>
              {backfill.enabled && (
                <>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      type="date"
                      label={t('data.from')}
                      value={backfill.start}
                      onChange={(e) => setBackfill({ ...backfill, start: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      type="date"
                      label={t('data.to')}
                      value={backfill.end}
                      onChange={(e) => setBackfill({ ...backfill, end: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      helperText={t('data.exclusiveTheFirstDayNotLoaded')}
                    />
                  </Box>
                  {estimate.days > 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {countLabel(estimate.days, 'day')} ×{' '}
                      {bytesLabel(estimate.perDay)} ≈ {bytesLabel(estimate.bytes)}{' '}{t('data.scanned')}{' '}{moneyLabel(estimate.bytes)}.
                      {estimate.unknown
                        ? t('data.someSourcesCouldNotBePricedSoTheRealFigureIs')
                        : t('data.anEstimateDaysDifferInSize')}
                    </Alert>
                  )}
                </>
              )}
            </Paper>
          )}

          <Button
            variant="contained"
            disabled={!canLeaveStep[4] || saving}
            onClick={save}
          >
            {saving ? t('data.saving') : editing ? t('data.saveChanges') : t('data.createSource')}
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 4 }}>
        <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>{t('data.back')}</Button>
        {activeStep < STEPS().length - 1 && (
          <Button
            variant="contained"
            disabled={!canLeaveStep[activeStep] || probe.isPending}
            onClick={goNext}
          >{t('data.next')}</Button>
        )}
      </Box>
    </Box>
  );
}
